/* app/pipeline.jsx - Reports (per-site, assembled from GSC + plans + BI + to-dos)
   and AI Assistant chat (POST /api/claude/chat, BI-context system prompt).
   Inbox and To-do live in pipeline-inbox.jsx / pipeline-todo.jsx. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useRef, useEffect, useCallback } = React;
  function MlIcon(props) { return React.createElement(window.MlIcon, props); }
  const DS = () => window.HuddleDesignSystem_775113;
  const fmtN = (n) => (n == null ? "-" : n >= 1000000 ? (n / 1000000).toFixed(1) + "M" : n >= 1000 ? (n / 1000).toFixed(1) + "K" : String(Math.round(n)));
  const pctOf = (c) => (c == null ? "-" : (c <= 1 ? (c * 100).toFixed(1) : c.toFixed(1)) + "%");

  // ---------- Reports (per-site, assembled from live endpoints, read-only) ----------
  const REL_LBL = { lookalike: ["Lookalike", "look"], complementary: ["Complementary", "comp"], alternative: ["Alternative", "alt"] };
  function Reports({ site }) {
    const { Button, StatCard, Badge } = DS();
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const rawUrl = (site && (site.urlFull || site.url)) || "";
    const fullUrl = rawUrl.startsWith("http") ? rawUrl : (rawUrl ? "https://" + rawUrl : "");
    const [d, setD] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
      if (siteId == null) { setLoading(false); return; }
      let cancel = false; setLoading(true);
      Promise.all([
        api(`/gsc/summary?site_id=${siteId}&range=28d`).catch(() => null),
        api(`/content-plans?site_id=${siteId}`).catch(() => []),
        api(`/bi/entities?site_id=${siteId}&entity_type=competitor&owner_flag=0`).catch(() => []),
        api(`/research/todo-hub?site_id=${siteId}`).catch(() => ({ items: [] })),
        fullUrl ? api(`/wp/audit?siteUrl=${encodeURIComponent(fullUrl)}&perPage=100`).catch(() => null) : Promise.resolve(null),
      ]).then(([gsc, plans, comps, hub, audit]) => {
        if (cancel) return;
        setD({ gsc, plans: Array.isArray(plans) ? plans : [], comps: Array.isArray(comps) ? comps : [], hub: (hub && hub.items) || [], audit });
        setLoading(false);
      });
      return () => { cancel = true; };
    }, [siteId, fullUrl]);

    const cur = (d && d.gsc && d.gsc.current) || {}, prev = (d && d.gsc && d.gsc.previous) || {};
    const delta = (c, p, lower) => { if (c == null || p == null || !p) return {}; const up = c >= p, good = lower ? !up : up; return { delta: Math.abs(Math.round((c - p) / p * 100)) + "%", deltaDirection: good ? "up" : "down" }; };
    const byStatus = (s) => (d ? d.plans.filter(p => p.status === s).length : 0);
    const sum = (d && d.audit && d.audit.summary) || {};
    const comps = (d && d.comps) || [];
    const openTodos = (d && d.hub || []).filter(i => i.status !== "done");

    return (
      <div style={{ maxWidth: 900 }}>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles"><h1 className="ml-pagehead__title"><MlIcon name="bar-chart" size={22} style={{ color: "var(--primary)" }} />Report</h1><p className="ml-pagehead__sub">Assembled overview for <strong>{site ? site.name : "this site"}</strong> · last 28 days · read-only</p></div>
          <div className="ml-pagehead__actions"><Button variant="secondary" leadingIcon="download" onClick={() => window.print()}>Export PDF</Button></div>
        </div>

        {loading ? <div className="bi-subempty">Assembling report…</div> : <>
        <div className="ml-report-section">
          <div className="ml-report-section__h"><MlIcon name="trending-up" size={18} />Search performance</div>
          <div className="ml-kwstats" style={{ marginBottom: 0 }}>
            <StatCard label="Clicks" value={fmtN(cur.clicks)} icon="trending-up" {...delta(cur.clicks, prev.clicks)} deltaNote="vs prev" />
            <StatCard label="Impressions" value={fmtN(cur.impressions)} icon="eye" {...delta(cur.impressions, prev.impressions)} />
            <StatCard label="Avg. CTR" value={pctOf(cur.ctr)} icon="target" {...delta(cur.ctr, prev.ctr)} />
            <StatCard label="Avg. position" value={cur.position != null ? cur.position.toFixed(1) : "-"} icon="bar-chart" {...delta(cur.position, prev.position, true)} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div className="hds-card hds-card--pad">
            <div className="ml-report-section__h" style={{ marginBottom: 14 }}><MlIcon name="file-text" size={18} />Content pipeline</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[["Published", byStatus("published"), "success"], ["Drafting", byStatus("drafting"), "warning"], ["In review", byStatus("in_review"), "info"], ["Ideas in planner", byStatus("idea"), "neutral"]].map(([l, v, tone]) => (
                <div key={l} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}><span style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>{l}</span><Badge tone={tone}>{v}</Badge></div>
              ))}
            </div>
          </div>
          <div className="hds-card hds-card--pad">
            <div className="ml-report-section__h" style={{ marginBottom: 14 }}><MlIcon name="gauge" size={18} />SEO health</div>
            {d && d.audit ? (
              <ul className="ml-tips" style={{ flex: 1 }}><li className="is-bad"><MlIcon name="x-circle" size={16} />{sum.error || 0} errors</li><li className="is-warn"><MlIcon name="alert-triangle" size={16} />{sum.warning || 0} warnings</li><li className="is-ok"><MlIcon name="check-circle" size={16} />{(d.audit.crawled || 0)} pages crawled</li></ul>
            ) : <div className="bi-subempty" style={{ padding: "8px 0" }}>Run a Site Audit to populate.</div>}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
          <div className="hds-card hds-card--pad">
            <div className="ml-report-section__h" style={{ marginBottom: 14 }}><MlIcon name="flag" size={18} />Competitors</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {comps.length === 0 ? <div className="bi-subempty" style={{ padding: "4px 0" }}>None tracked yet.</div> :
                comps.slice(0, 5).map(c => { const r = ((c.data && c.data.relationships_to_me) || [])[0]; const lbl = r && REL_LBL[r]; return <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}><span style={{ fontSize: "var(--text-sm)", color: "var(--text-primary)", fontWeight: 500 }}>{c.name}</span>{lbl && <span className={"hds-badge hds-badge--rel-" + lbl[1]}>{lbl[0]}</span>}</div>; })}
            </div>
          </div>
          <div className="hds-card hds-card--pad">
            <div className="ml-report-section__h" style={{ marginBottom: 14 }}><MlIcon name="check-square" size={18} />Open to-dos <span className="bi-conf">{openTodos.length}</span></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {openTodos.length === 0 ? <div className="bi-subempty" style={{ padding: "4px 0" }}>All clear.</div> :
                openTodos.slice(0, 6).map(t => <div key={t.uid} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}><MlIcon name="check-square" size={14} style={{ color: "var(--text-disabled)" }} />{t.title}</div>)}
            </div>
          </div>
        </div>
        </>}
      </div>
    );
  }

  // ---------- AI Assistant chat ----------
  function Assistant({ site }) {
    const { Button, IconButton } = DS();
    const [msgs, setMsgs] = useState([]);
    const [typing, setTyping] = useState(false);
    const [text, setText] = useState("");
    const scrollRef = useRef(null);
    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, typing]);

    const send = async (preset) => {
      const q = preset || text.trim(); if (!q) return;
      setText(""); const next = [...msgs, { role: "user", text: q }]; setMsgs(next); setTyping(true);
      try {
        const res = await api(`/claude/chat`, { method: "POST", body: JSON.stringify({
          system: `You are a content-strategy assistant for ${site ? site.name : "this site"}, a WordPress site managed in Marketlens. Give concise, actionable SEO and content guidance. When useful, suggest which Marketlens module (Competitors, Keywords, Content Planner, etc.) to open next.`,
          messages: next.map(m => ({ role: m.role === "ai" ? "assistant" : "user", content: m.text })),
          max_tokens: 800,
        }) });
        const txt = (res && res.content && res.content[0] && res.content[0].text) || "(no response)";
        setMsgs(m => [...m, { role: "ai", text: txt }]);
      } catch (e) {
        // A 400 from our own route (no Anthropic key, read-only role) is the
        // answer, not a transport failure: show it as it is, rather than
        // burying the next step under "couldn't reach the assistant".
        const msg = e.message || "";
        const own = (e.status === 400 || e.status === 403) && msg;
        setMsgs(m => [...m, { role: "ai", text: own ? msg : "Sorry - I couldn't reach the assistant. " + msg }]);
      }
      finally { setTyping(false); }
    };
    const suggestions = ["Where are we losing keyword overlap this quarter?", "Draft a content plan for our weakest cluster", "Summarise this month's performance"];

    return (
      <div>
        <div className="ml-pagehead" style={{ marginBottom: 14 }}><div className="ml-pagehead__titles"><h1 className="ml-pagehead__title"><MlIcon name="message-circle" size={22} style={{ color: "var(--primary)" }} />AI Assistant</h1><p className="ml-pagehead__sub">Content-strategy chat grounded in your BI graph and performance data.</p></div></div>

        <div className="ml-chatsurface">
          <div className="ml-chatsurface__head">
            <span className="ml-msg__avatar ml-msg__avatar--ai" style={{ width: 30, height: 30 }}><MlIcon name="sparkles" size={16} /></span>
            <div style={{ flex: 1 }}><div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text-primary)" }}>Strategy assistant</div><div style={{ fontSize: 11, color: "var(--text-muted)" }}>Grounded in {site.name}</div></div>
            <span className="ml-aibadge"><span className="ml-aibadge__dot" />Connected</span>
          </div>

          <div className="ml-chatsurface__scroll" ref={scrollRef}>
            {msgs.length === 0 ? (
              <div className="ml-chat-empty">
                <span className="ml-stub__icon" style={{ margin: "0 auto" }}><MlIcon name="message-circle" size={26} /></span>
                <div className="ml-stub__title" style={{ marginTop: 14 }}>Ask about your sites</div>
                <p className="ml-stub__sub" style={{ margin: "6px auto 0" }}>Grounded in your Business Intelligence graph, keywords and Search Console data.</p>
                <div className="ml-chat-suggest">{suggestions.map(s => <button key={s} onClick={() => send(s)}><MlIcon name="sparkles" size={16} />{s}</button>)}</div>
              </div>
            ) : (
              <div className="ml-chat">
                {msgs.map((m, i) => m.role === "user" ? (
                  <div className="ml-msg ml-msg--user" key={i}><span className="ml-msg__avatar ml-msg__avatar--user"><MlIcon name="user" size={15} /></span><div className="ml-bubble ml-bubble--user">{m.text}</div></div>
                ) : (
                  <div className="ml-msg" key={i}><span className="ml-msg__avatar ml-msg__avatar--ai"><MlIcon name="sparkles" size={15} /></span><div style={{ minWidth: 0 }}><div className="ml-bubble ml-bubble--ai">{m.text}</div>{m.chips && <div className="ml-chipset" style={{ marginTop: 8 }}>{m.chips.map(c => <span key={c} className="ml-chip" style={{ height: 26, cursor: "default" }}>{c}</span>)}</div>}</div></div>
                ))}
                {typing && <div className="ml-msg"><span className="ml-msg__avatar ml-msg__avatar--ai"><MlIcon name="sparkles" size={15} /></span><div className="ml-bubble ml-bubble--ai"><span className="ml-typing"><span></span><span></span><span></span></span></div></div>}
              </div>
            )}
          </div>

          <div className="ml-chatsurface__foot">
            <div className="ml-composer">
              <textarea rows={1} placeholder="Ask about your sites, keywords or competitors…" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
              {/* leadingIcon, not children: the DS Button wraps children in a <span> and
                  takes a text button's side padding, which is what left this glyph
                  sitting off-centre. .ml-composer .hds-btn squares it off. */}
              <Button className="ml-composer__send" variant="primary" size="sm" aria-label="Send" onClick={() => send()} leadingIcon={<MlIcon name="send" size={16} />} />
            </div>
            <div className="ml-composer__ctx" style={{ paddingBottom: 0, marginTop: 8 }}>
              <span className="ml-chip ml-chip--selected" style={{ height: 26 }}><MlIcon name="globe" size={13} />{site.name}</span>
              <span className="ml-chip" style={{ height: 26 }}><MlIcon name="flag" size={13} />BI graph</span>
              <span className="ml-chip" style={{ height: 26 }}><MlIcon name="trending-up" size={13} />GSC data</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  window.MlPipeline = { Reports, Assistant };
})();
